/**
 * Fine-grained permissions and the default role → permission mapping.
 * Mirrors the access-control matrix in documents/architecture/roles.md.
 *
 * Permissions are the unit guards check against — prefer them over raw
 * role checks in UI/routes so authority can be tuned without touching
 * call sites. A user may also carry explicit `permissions` that extend
 * their role defaults (see lib/rbac/access.ts).
 */
import { UserRole } from './roles'

export const Permission = {
  // ── Platform administration (super admin only) ──────────────
  SystemSettingsManage: 'system.settings.manage',
  /** Ghost-login: act as another user to see what they see. */
  UserImpersonate: 'user.impersonate',
  /**
   * Read the platform-wide impersonation log (AMLIQ-218 / AMLIQ-222).
   * Separate from {@link UserImpersonate} on purpose — viewing the audit record
   * is a different authority from being able to enter an account.
   */
  ImpersonationLogView: 'impersonation-log.view',

  // ── Subscriber accounts / tenants — cross-account (super admin) ──
  AccountView: 'account.view',
  /** Set up a new account. */
  AccountCreate: 'account.create',
  /** Manage / suspend any account across the platform. */
  AccountManage: 'account.manage',

  // ── Own account profile, subscription & settings (admin user) ──
  AccountSettingsManage: 'account-settings.manage',
  BillingView: 'billing.view',
  BillingManage: 'billing.manage',

  // ── Users — invite / edit roles / suspend / revoke ──────────
  UserView: 'user.view',
  UserManage: 'user.manage',

  // ── Verification checks ─────────────────────────────────────
  /** Submit a new check. */
  CheckSubmit: 'check.submit',
  /** View one's own submitted checks. */
  CheckViewOwn: 'check.view.own',
  /** View every check in the account (also gates the Assignee column). */
  CheckViewAll: 'check.view.all',
  /**
   * Export the filtered check list as CSV/XLSX (`GET /kyc/checks/export`).
   * Super admin only for now — granted via `ALL_PERMISSIONS`; add to other
   * roles' defaults below to widen access without touching call sites.
   */
  CheckExport: 'check.export',

  // ── Reports & analytics (account-wide) ──────────────────────
  ReportView: 'report.view',

  // ── Learn Centre ────────────────────────────────────────────
  /** Upload, edit and publish Learn Centre resources (super admin). */
  LearnCentreManage: 'learn-centre.manage',
} as const

export type Permission = (typeof Permission)[keyof typeof Permission]

export const ALL_PERMISSIONS: ReadonlyArray<Permission> = Object.values(Permission)

/**
 * Default permissions granted to each role — see the matrix in
 * documents/architecture/roles.md. Each row of that matrix maps to a permission here.
 */
export const ROLE_PERMISSIONS: Record<UserRole, ReadonlyArray<Permission>> = {
  // Super admin: everything, including cross-account access & ghost-login.
  [UserRole.SystemAdmin]: ALL_PERMISSIONS,

  // Admin user: top admin within an account — manages users, account
  // profile/settings & subscription, and sees all checks.
  [UserRole.AdminUser]: [
    Permission.CheckSubmit,
    Permission.CheckViewOwn,
    Permission.CheckViewAll,
    Permission.UserView,
    Permission.UserManage,
    Permission.AccountSettingsManage,
    Permission.BillingView,
    Permission.BillingManage,
    Permission.ReportView,
  ],

  // Compliance officer: like Admin but WITHOUT account profile/settings
  // & billing (the one differentiator in the matrix).
  [UserRole.ComplianceOfficer]: [
    Permission.CheckSubmit,
    Permission.CheckViewOwn,
    Permission.CheckViewAll,
    Permission.UserView,
    Permission.UserManage,
    Permission.ReportView,
  ],

  // Standard user: submit and view only their own checks.
  [UserRole.StandardUser]: [Permission.CheckSubmit, Permission.CheckViewOwn],
}
